import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload, useLocation, useNavigate } from 'react-router-dom';

type Ctx = {
  dirty: boolean;
  setDirty: (v: boolean) => void;
  clearDirty: () => void;
  markDirty: () => void;
  guardedNavigate: ReturnType<typeof useNavigate>;
};

const UnsavedCtx = createContext<Ctx | null>(null);
export const useUnsavedChanges = () => {
  const c = useContext(UnsavedCtx);
  if (!c) throw new Error('useUnsavedChanges must be used inside <UnsavedChangesProvider>');
  return c;
};

// Global events (unchanged)
export const markDirty  = () => window.dispatchEvent(new CustomEvent('uc-mark-dirty'));
export const clearDirty = () => window.dispatchEvent(new CustomEvent('uc-clear-dirty'));
export const markSaved  = () => window.dispatchEvent(new CustomEvent('uc-snapshot'));

type TrackEl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
const isTrackEl = (el: Element | null): el is TrackEl =>
  !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');

const CRITICAL_SELECTOR = '[data-uc-critical], [data-critical]';
const isCritical = (el: Element | null) => !!el && !!el.closest(CRITICAL_SELECTOR);

const getElValue = (el: TrackEl): string => {
  // allow opt-out
  if (el.closest('[data-no-dirty]')) return '__IGNORE__';

  if (el.tagName === 'INPUT') {
    const inp = el as HTMLInputElement;
    const type = (inp.type || '').toLowerCase();
    if (['button','submit','reset','image','hidden'].includes(type)) return '__IGNORE__';
    if (type === 'checkbox' || type === 'radio') return inp.checked ? '1' : '0';
    if (type === 'file') return inp.files?.length ? `file:${inp.files.length}` : '';
    return inp.value ?? '';
  }
  if (el.tagName === 'TEXTAREA') return (el as HTMLTextAreaElement).value ?? '';

  const sel = el as HTMLSelectElement;
  if (sel.multiple) {
    const vals = Array.from(sel.selectedOptions).map(o => o.value).sort();
    return `multi:[${vals.join(',')}]`;
  }
  return sel.value ?? '';
};

const getOrigin = (el: TrackEl): string => {
  const stored = el.getAttribute('data-uc-origin');
  if (stored != null) return stored;

  // defaults for first snapshot
  if (el.tagName === 'INPUT') {
    const i = el as HTMLInputElement;
    const t = (i.type || '').toLowerCase();
    if (t === 'checkbox' || t === 'radio') return i.defaultChecked ? '1' : '0';
    return i.defaultValue ?? '';
  }
  if (el.tagName === 'TEXTAREA') return (el as HTMLTextAreaElement).defaultValue ?? '';

  const s = el as HTMLSelectElement;
  if (s.multiple) {
    const vals = Array.from(s.options).filter(o => o.defaultSelected).map(o => o.value).sort();
    return `multi:[${vals.join(',')}]`;
  }
  const def = Array.from(s.options).find(o => o.defaultSelected)?.value;
  return def ?? s.value ?? '';
};

const setOriginToCurrent = (el: TrackEl) => {
  const v = getElValue(el);
  el.setAttribute('data-uc-origin', v);
};

// *** critical-only compute ***
const computeIsDirty = (root: HTMLElement | null): boolean => {
  if (!root) return false;
  // only scan critical zones to keep this cheap & predictable
  const scope = root.querySelectorAll<HTMLElement>(CRITICAL_SELECTOR);
  if (!scope.length) return false; // nothing marked critical => nothing to guard

  for (const zone of Array.from(scope)) {
    const els = zone.querySelectorAll<TrackEl>('input, textarea, select');
    for (const el of Array.from(els)) {
      if (el.closest('[data-no-dirty]')) continue;
      const cur = getElValue(el);
      if (cur === '__IGNORE__') continue;
      const origin = getOrigin(el);
      if (cur !== origin) return true;
    }
  }
  return false;
};

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode; autoTrackForms?: boolean }> = ({
  children,
  autoTrackForms = true,
}) => {
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);
  const pendingRef = useRef<null | { url: string; opts?: any }>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Guard-aware navigate (only opens on actual route change)
  const guardedNavigate = (to: any, opts?: any) => {
    if (dirty) {
      pendingRef.current = { url: to, opts };
      setOpen(true);
    } else {
      navigate(to, opts);
    }
  };

  // Native browser guard on refresh/close (shows the small browser prompt)
  useBeforeUnload(
    React.useCallback(
      (e) => {
        if (!dirty) return;
        e.preventDefault();
        e.returnValue = '';
      },
      [dirty]
    ),
    { capture: true }
  );

  // ---- Snapshot helpers ----
  const snapshotAll = React.useCallback(() => {
    if (!rootRef.current) return;
    const zones = rootRef.current.querySelectorAll<HTMLElement>(CRITICAL_SELECTOR);
    for (const zone of Array.from(zones)) {
      const els = zone.querySelectorAll<TrackEl>('input, textarea, select');
      for (const el of Array.from(els)) {
        if (el.closest('[data-no-dirty]')) continue;
        setOriginToCurrent(el);
      }
    }
  }, []);

  const recalcDirty = React.useCallback(() => {
    setDirty(computeIsDirty(rootRef.current));
  }, []);

  // On route change: consider page clean + snapshot current criticals
  useEffect(() => {
    snapshotAll();
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Auto-track only critical inputs
  useEffect(() => {
    if (!autoTrackForms) return;

    const onInput = (e: Event) => {
      const tgt = e.target as Element | null;
      if (!isTrackEl(tgt) || !isCritical(tgt)) return;
      if (tgt.closest('[data-no-dirty]')) return;
      recalcDirty();
    };

    const onSubmit = (e: Event) => {
      // Successful form submit = saved ⇒ snapshot & clear
      // (if you handle save manually, call markSaved() instead)
      snapshotAll();
      setDirty(false);
    };

    const root = rootRef.current ?? document;
    root.addEventListener('input', onInput, true);
    root.addEventListener('change', onInput, true);
    root.addEventListener('submit', onSubmit, true);

    return () => {
      root.removeEventListener('input', onInput, true);
      root.removeEventListener('change', onInput, true);
      root.removeEventListener('submit', onSubmit, true);
    };
  }, [autoTrackForms, recalcDirty, snapshotAll]);

  // Manual global events
  useEffect(() => {
    const md = () => setDirty(true);
    const cd = () => setDirty(false);
    const ss = () => { snapshotAll(); setDirty(false); };

    window.addEventListener('uc-mark-dirty', md as any);
    window.addEventListener('uc-clear-dirty', cd as any);
    window.addEventListener('uc-snapshot', ss as any);

    return () => {
      window.removeEventListener('uc-mark-dirty', md as any);
      window.removeEventListener('uc-clear-dirty', cd as any);
      window.removeEventListener('uc-snapshot', ss as any);
    };
  }, [snapshotAll]);

useEffect(() => {
  const onClick = (e: MouseEvent) => {
    if (!dirty) return;
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey || e.ctrlKey || e.altKey || e.shiftKey
    ) return;

    const el = (e.target as HTMLElement | null)?.closest('a[href]');
    if (!(el instanceof HTMLAnchorElement)) return; // <-- proper narrowing

    const url = new URL(el.href, window.location.origin); // now .href is OK
    const sameOrigin = url.origin === window.location.origin;

    const next = url.pathname + url.search + url.hash;
    const curr = window.location.pathname + window.location.search + window.location.hash;

    if (sameOrigin && next !== curr) {
      e.preventDefault();
      pendingRef.current = { url: next };
      setOpen(true);
    }
  };

  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}, [dirty]);


  const stayHere = () => {
    setOpen(false);
    pendingRef.current = null;
  };

  const discardAndGo = () => {
    setOpen(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    // allow nav; next page will snapshot on mount
    if (pending?.url) navigate(pending.url, pending.opts);
  };

  const value = useMemo<Ctx>(
    () => ({
      dirty,
      setDirty,
      clearDirty: () => setDirty(false),
      markDirty: () => setDirty(true),
      guardedNavigate,
    }),
    [dirty]
  );

  return (
    <UnsavedCtx.Provider value={value}>
      <div ref={rootRef} data-uc-root>
        {children}
      </div>

      {/* Only shows on actual exit attempts */}
      {open && (
        <div className="fixed inset-0 z-[999] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-[92vw] max-w-sm p-5 text-right">
            <h3 className="text-lg font-bold mb-2">יש שינויים שלא נשמרו</h3>
            <p className="text-sm text-gray-600 mb-4">האם לשמור או לבטל לפני היציאה?</p>
            <div className="flex gap-2 justify-end">
              <button onClick={stayHere} className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300">
                הישאר
              </button>
              <button onClick={discardAndGo} className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700">
                בטל שינויים
              </button>
            </div>
          </div>
        </div>
      )}
    </UnsavedCtx.Provider>
  );
};
